import './assets/styles.css';

import { Command, Plugin } from '@ckeditor/ckeditor5-core';
import { ButtonView } from '@ckeditor/ckeditor5-ui';
import { Widget, toWidget, toWidgetEditable } from '@ckeditor/ckeditor5-widget';

export default class ThreeColumsBox extends Plugin {
    static get requires() {
        return [ ThreeColumnsBoxEditing, ThreeColumnsBoxUi ];
    }
}

class ThreeColumnsBoxUi extends Plugin {
    init() {
        const editor = this.editor;
        const t = editor.t;

        editor.ui.componentFactory.add( 'ThreeColumnsBox', locale => {
            // The state of the button will be bound to the widget command.
            const command = editor.commands.get( 'insertThreeColumnsBox' );

            // The button will be an instance of ButtonView.
            const buttonView = new ButtonView( locale );

            buttonView.set( {
                // The t() function helps localize the editor. All strings enclosed in t() can be
                // translated and change when the language of the editor changes.
                label: t( '3☐' ),
                withText: true,
                tooltip: true
            } );

            // Bind the state of the button to the command.
            buttonView.bind( 'isOn', 'isEnabled' ).to( command, 'value', 'isEnabled' );

            // Execute the command when the button is clicked (executed).
            this.listenTo( buttonView, 'execute', () => editor.execute( 'insertThreeColumnsBox' ) );

            return buttonView;
        } );
    }
}

class ThreeColumnsBoxEditing extends Plugin {
    static get requires() {
        return [ Widget ];
    }

    init() {

        this._defineSchema();
        this._defineConverters();

        this.editor.commands.add( 'insertThreeColumnsBox', new InsertThreeColumnsBoxCommand( this.editor ) );
    }

    _defineSchema() {
        const schema = this.editor.model.schema;

        schema.register( 'ThreeColumnsBox', {
      
            inheritAllFrom: '$blockObject'
        } );

        schema.register( 'ThreeColumnsBoxCol1', {
        
            isLimit: true,

            allowIn: 'ThreeColumnsBox',

         
            allowContentOf: '$root'
        } );

        schema.register( 'ThreeColumnsBoxCol2', {
          
            isLimit: true,

            allowIn: 'ThreeColumnsBox',

  
            allowContentOf: '$root'
        } );

        schema.register( 'ThreeColumnsBoxCol3', {
          
            isLimit: true,

            allowIn: 'ThreeColumnsBox',

  
            allowContentOf: '$root'
        } );

        schema.addChildCheck( ( context, childDefinition ) => {
            if ( context.endsWith( 'ThreeColumnsBoxCol3' ) && childDefinition.name == 'ThreeColumnsBox' ) {
                return false;
            }
        } );
    }

    _defineConverters() {
        const conversion = this.editor.conversion;

        // <simpleBox> converters
        conversion.for( 'upcast' ).elementToElement( {
            model: 'ThreeColumnsBox',
            view: {
                name: 'section',
                classes: 'three-columns-box'
            }
        } );
        conversion.for( 'dataDowncast' ).elementToElement( {
            model: 'ThreeColumnsBox',
            view: {
                name: 'section',
                classes: 'three-columns-box'
            }
        } );
        conversion.for( 'editingDowncast' ).elementToElement( {
            model: 'ThreeColumnsBox',
            view: ( modelElement, { writer: viewWriter } ) => {
                const section = viewWriter.createContainerElement( 'section', { class: 'three-columns-box' } );

                return toWidget( section, viewWriter, { label: 'Three columns widget' } );
            }
        } );

        // <simpleBoxTitle> converters
        conversion.for( 'upcast' ).elementToElement( {
            model: 'ThreeColumnsBoxCol1',
            view: {
                name: 'div',
                classes: 'three-columns-box__col'
            }
        } );
        conversion.for( 'dataDowncast' ).elementToElement( {
            model: 'ThreeColumnsBoxCol1',
            view: {
                name: 'div',
                classes: 'three-columns-box__col'
            }
        } );
        conversion.for( 'editingDowncast' ).elementToElement( {
            model: 'ThreeColumnsBoxCol1',
            view: ( modelElement, { writer: viewWriter } ) => {
               
                const div = viewWriter.createEditableElement( 'div', { class: 'three-columns-box__col' } );

                return toWidgetEditable( div, viewWriter );
            }
        } );

        // <simpleBoxDescription> converters
        conversion.for( 'upcast' ).elementToElement( {
            model: 'ThreeColumnsBoxCol2',
            view: {
                name: 'div',
                classes: 'three-columns-box__col'
            }
        } );
        conversion.for( 'dataDowncast' ).elementToElement( {
            model: 'ThreeColumnsBoxCol2',
            view: {
                name: 'div',
                classes: 'three-columns-box__col'
            }
        } );
        conversion.for( 'editingDowncast' ).elementToElement( {
            model: 'ThreeColumnsBoxCol2',
            view: ( modelElement, { writer: viewWriter } ) => {
            
                const div = viewWriter.createEditableElement( 'div', { class: 'three-columns-box__col' } );

                return toWidgetEditable( div, viewWriter );
            }
        } );

        conversion.for( 'upcast' ).elementToElement( {
            model: 'ThreeColumnsBoxCol3',
            view: {
                name: 'div',
                classes: 'three-columns-box__col'
            }
        } );
        conversion.for( 'dataDowncast' ).elementToElement( {
            model: 'ThreeColumnsBoxCol3',
            view: {
                name: 'div',
                classes: 'three-columns-box__col'
            }
        } );
        conversion.for( 'editingDowncast' ).elementToElement( {
            model: 'ThreeColumnsBoxCol3',
            view: ( modelElement, { writer: viewWriter } ) => {
            
                const div = viewWriter.createEditableElement( 'div', { class: 'three-columns-box__col' } );

                return toWidgetEditable( div, viewWriter );
            }
        } );
    }
}

class InsertThreeColumnsBoxCommand extends Command {
    execute() {
        this.editor.model.change( writer => {
            this.editor.model.insertObject( createThreeColumnsBox( writer ) );
        } );
    }

    refresh() {
        const model = this.editor.model;
        const selection = model.document.selection;
        const allowedIn = model.schema.findAllowedParent( selection.getFirstPosition(), 'ThreeColumnsBox' );

        this.isEnabled = allowedIn !== null;
    }
}

function createThreeColumnsBox( writer ) {
    const ThreeColumnsBox = writer.createElement( 'ThreeColumnsBox' );
    const ThreeColumnsBoxCol1 = writer.createElement( 'ThreeColumnsBoxCol1' );
    const ThreeColumnsBoxCol2 = writer.createElement( 'ThreeColumnsBoxCol2' );
    const ThreeColumnsBoxCol3 = writer.createElement( 'ThreeColumnsBoxCol3' );

    writer.append( ThreeColumnsBoxCol1, ThreeColumnsBox );
    writer.append( ThreeColumnsBoxCol2, ThreeColumnsBox );
    writer.append( ThreeColumnsBoxCol3, ThreeColumnsBox );

    // There must be at least one paragraph for the description to be editable.
    // See https://github.com/ckeditor/ckeditor5/issues/1464.
    writer.appendElement( 'paragraph', ThreeColumnsBoxCol1 );
    writer.appendElement( 'paragraph', ThreeColumnsBoxCol2 );
    writer.appendElement( 'paragraph', ThreeColumnsBoxCol3 );

    return ThreeColumnsBox;
}
